import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Search, Download, Star, MessageSquareQuote, CheckCircle2, RefreshCw, Plus, Settings2, X, Loader2,
    AlertCircle, PhoneCall, MessageCircle, Smile, Clock, ExternalLink, UserRound, Receipt, ThumbsUp, ThumbsDown,
} from 'lucide-react';
import styles from './FeedbackManagement.module.css';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import { SMIIFY_API } from '../../config/api';

const API = `${SMIIFY_API}/feedback`;
const TABS = [['all', 'All'], ['attention', 'Needs attention'], ['positive', 'Positive'], ['callback', 'Call requested']];
const RANGES = [[7, '7 days'], [30, '30 days'], [90, '90 days'], [365, '1 year']];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '');
const maskPhone = (p) => { const s = String(p || ''); return s.length >= 6 ? `${s.slice(0, 2)}••••••${s.slice(-2)}` : s; };

const Stars = ({ value, size = 12 }) => (
    <span className={styles.stars} aria-label={`${value} out of 5`}>
        {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={size} className={n <= Math.round(value) ? styles.starOn : styles.starOff} />)}
    </span>
);

/* =============================== settings =============================== */
const SettingsModal = ({ headers, onClose, onSaved }) => {
    const [s, setS] = useState(null);
    const [err, setErr] = useState('');
    const [saving, setSaving] = useState(false);
    const [star, setStar] = useState(5);
    const [chipText, setChipText] = useState('');

    useEffect(() => {
        fetch(`${API}/settings`, { headers }).then((r) => r.json()).then((j) => { if (j.success) setS(j.data); else setErr(j.message || 'Could not load.'); }).catch(() => setErr('Cannot reach the server.'));
    }, [headers]);

    const set = (patch) => setS((x) => ({ ...x, ...patch }));
    const testUrl = s ? (/^https:\/\//.test(s.googleReviewUrl) ? s.googleReviewUrl : s.googlePlaceId ? `https://search.google.com/local/writereview?placeid=${encodeURIComponent(s.googlePlaceId)}` : '') : '';
    const addChip = () => {
        const t = chipText.trim();
        if (!t) return;
        if ((s.chips[star] || []).some((x) => x.toLowerCase() === t.toLowerCase())) { setChipText(''); return; }
        set({ chips: { ...s.chips, [star]: [...(s.chips[star] || []), t].slice(0, 12) } });
        setChipText('');
    };

    const save = async () => {
        setSaving(true); setErr('');
        try {
            const res = await fetch(`${API}/settings`, { method: 'PUT', headers, body: JSON.stringify(s) });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) throw new Error(j.message || 'Could not save.');
            onSaved(j.message);
        } catch (e) { setErr(e.message); } finally { setSaving(false); }
    };

    return (
        <div className={styles.overlay} role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Feedback settings">
                <div className={styles.modalHead}>
                    <div><h3>Feedback settings</h3><p>{s?.isHQ ? 'HQ: used by every branch without its own.' : s?.source === 'HQ' ? 'This branch follows HQ. Saving gives it its own.' : 'Settings for this branch.'}</p></div>
                    <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close"><X size={16} /></button>
                </div>
                {!s ? <div className={styles.pad}>{err ? <p className={styles.err}>{err}</p> : <Loader2 size={18} className={styles.spin} />}</div> : (
                    <div className={styles.modalBody}>
                        <section>
                            <h4>Google reviews</h4>
                            <label className={styles.field}><span>Google Place ID</span>
                                <input value={s.googlePlaceId} onChange={(e) => set({ googlePlaceId: e.target.value.trim() })} placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4" />
                            </label>
                            <label className={styles.field}><span>Or the "write a review" link</span>
                                <input value={s.googleReviewUrl} onChange={(e) => set({ googleReviewUrl: e.target.value.trim() })} placeholder="https://g.page/r/xxxxxxxx/review" />
                            </label>
                            <div className={styles.row}>
                                {testUrl ? <a href={testUrl} target="_blank" rel="noreferrer" className={styles.ghostBtn}><ExternalLink size={13} /> Test link</a> : <small className={styles.hint}>Find the Place ID with Google&apos;s "Place ID Finder", or copy "Ask for reviews" link from your Google Business Profile.</small>}
                            </div>
                            <label className={styles.check}><input type="checkbox" checked={s.googleEnabled} onChange={(e) => set({ googleEnabled: e.target.checked })} /> Ask happy clients to review on Google</label>
                            <div className={styles.grid2}>
                                <label className={styles.field}><span>Send to Google for</span>
                                    <select value={s.googleMinRating} onChange={(e) => set({ googleMinRating: Number(e.target.value) })} disabled={!s.googleEnabled}>
                                        <option value={5}>5 stars only</option><option value={4}>4 and 5 stars</option>
                                    </select>
                                </label>
                                <label className={styles.check} style={{ alignSelf: 'end' }}><input type="checkbox" checked={s.googleAutoRedirect} onChange={(e) => set({ googleAutoRedirect: e.target.checked })} disabled={!s.googleEnabled} /> Open Google by itself</label>
                            </div>
                        </section>
                        <section>
                            <h4>Form</h4>
                            <div className={styles.grid2}>
                                <label className={styles.field}><span>Offer a call back below</span>
                                    <select value={s.callbackBelow} onChange={(e) => set({ callbackBelow: Number(e.target.value) })}>
                                        <option value={0}>Never</option><option value={2}>1 star</option><option value={3}>1-2 stars</option><option value={4}>1-3 stars</option>
                                    </select>
                                </label>
                                <label className={styles.field}><span>Link works for (days)</span>
                                    <input type="number" min={1} max={90} value={s.linkDays} onChange={(e) => set({ linkDays: Number(e.target.value) })} />
                                </label>
                            </div>
                            <label className={styles.field}><span>Thank-you message (optional)</span>
                                <input value={s.thankYouText} maxLength={300} onChange={(e) => set({ thankYouText: e.target.value })} placeholder="Leave empty for a message that fits the rating" />
                            </label>
                        </section>
                        <section>
                            <h4>Question and options for each rating</h4>
                            <div className={styles.starTabs} role="tablist">
                                {[5, 4, 3, 2, 1].map((n) => (
                                    <button type="button" role="tab" key={n} aria-selected={star === n} className={star === n ? styles.starTabOn : ''} onClick={() => setStar(n)}>{n} <Star size={11} className={styles.starOn} /></button>
                                ))}
                            </div>
                            <label className={styles.field}><span>Question</span>
                                <input value={s.questions[star]} maxLength={140} onChange={(e) => set({ questions: { ...s.questions, [star]: e.target.value } })} />
                            </label>
                            <div className={styles.chipEdit}>
                                {(s.chips[star] || []).map((t) => (
                                    <span key={t} className={styles.tag}>{t}<button type="button" onClick={() => set({ chips: { ...s.chips, [star]: s.chips[star].filter((x) => x !== t) } })} aria-label={`Remove ${t}`}><X size={11} /></button></span>
                                ))}
                                <input value={chipText} onChange={(e) => setChipText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChip(); } }} placeholder="Add option and press Enter" aria-label="Add option" />
                            </div>
                        </section>
                        {err && <p className={styles.err}>{err}</p>}
                    </div>
                )}
                <div className={styles.modalFoot}>
                    <button type="button" className={styles.ghostBtn} onClick={onClose}>Cancel</button>
                    <button type="button" className={styles.primaryBtn} onClick={save} disabled={!s || saving}>{saving && <Loader2 size={13} className={styles.spin} />} Save settings</button>
                </div>
            </div>
        </div>
    );
};

/* =============================== add by hand =============================== */
const AddModal = ({ headers, onClose, onSaved }) => {
    const [f, setF] = useState({ customerName: '', phone: '', rating: 0, comment: '', staffName: '', invoiceNo: '' });
    const [err, setErr] = useState('');
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true); setErr('');
        try {
            const res = await fetch(API, { method: 'POST', headers, body: JSON.stringify(f) });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) throw new Error(j.message || 'Could not save.');
            onSaved(j.message);
        } catch (e) { setErr(e.message); } finally { setSaving(false); }
    };
    return (
        <div className={styles.overlay} role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Add feedback" style={{ maxWidth: 460 }}>
                <div className={styles.modalHead}><div><h3>Add feedback</h3><p>Taken by phone or at the counter.</p></div><button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close"><X size={16} /></button></div>
                <div className={styles.modalBody}>
                    <div className={styles.grid2}>
                        <label className={styles.field}><span>Client name</span><input value={f.customerName} onChange={(e) => setF({ ...f, customerName: e.target.value })} /></label>
                        <label className={styles.field}><span>Mobile</span><input value={f.phone} inputMode="numeric" maxLength={10} onChange={(e) => setF({ ...f, phone: e.target.value.replace(/\D/g, '') })} /></label>
                    </div>
                    <div className={styles.field}><span>Rating</span>
                        <div className={styles.pickStars} role="radiogroup" aria-label="Rating">
                            {[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} role="radio" aria-checked={f.rating === n} aria-label={`${n} stars`} onClick={() => setF({ ...f, rating: n })}><Star size={26} className={n <= f.rating ? styles.starOn : styles.starOff} /></button>)}
                        </div>
                    </div>
                    <div className={styles.grid2}>
                        <label className={styles.field}><span>Stylist (optional)</span><input value={f.staffName} onChange={(e) => setF({ ...f, staffName: e.target.value })} /></label>
                        <label className={styles.field}><span>Bill no. (optional)</span><input value={f.invoiceNo} onChange={(e) => setF({ ...f, invoiceNo: e.target.value })} /></label>
                    </div>
                    <label className={styles.field}><span>What the client said</span><textarea rows={3} value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} /></label>
                    {err && <p className={styles.err}>{err}</p>}
                </div>
                <div className={styles.modalFoot}>
                    <button type="button" className={styles.ghostBtn} onClick={onClose}>Cancel</button>
                    <button type="button" className={styles.primaryBtn} onClick={save} disabled={saving || !f.customerName.trim() || !f.rating}>{saving && <Loader2 size={13} className={styles.spin} />} Add feedback</button>
                </div>
            </div>
        </div>
    );
};

/* =============================== page =============================== */
const FeedbackManagement = () => {
    const { activeStore } = useStore();
    const { hasPermission } = useAuth();
    const storeId = activeStore?._id || activeStore?.id || 'global_hq';
    const canSeePhone = hasPermission ? hasPermission('customers', 'view_phone') : true;
    const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId }), [storeId]);

    const [days, setDays] = useState(30);
    const [tab, setTab] = useState('all');
    const [q, setQ] = useState('');
    const [ratingFilter, setRatingFilter] = useState(0);
    const [staffFilter, setStaffFilter] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(null);          // full feedback in the drawer
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [modal, setModal] = useState('');          // settings | add
    const [toast, setToast] = useState('');

    const flash = (t) => { setToast(t); setTimeout(() => setToast(''), 3500); };

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const qs = new URLSearchParams({ days, tab, limit: 200, ...(q.trim() ? { q: q.trim() } : {}), ...(ratingFilter ? { rating: ratingFilter } : {}), ...(staffFilter ? { staff: staffFilter } : {}) });
            const res = await fetch(`${API}?${qs}`, { headers });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) throw new Error(j.message || `Could not load (error ${res.status}).`);
            setData(j);
        } catch (e) { setError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message); } finally { setLoading(false); }
    }, [headers, days, tab, q, ratingFilter, staffFilter]);
    useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

    const openOne = async (id) => {
        setOpen({ loading: true }); setNote('');
        try {
            const res = await fetch(`${API}/${id}`, { headers });
            const j = await res.json();
            if (!j.success) throw new Error(j.message);
            setOpen(j.data); setNote(j.data.resolution?.note || '');
        } catch (e) { setOpen({ error: e.message || 'Could not load.' }); }
    };
    const resolve = async (status) => {
        setBusy(true);
        try {
            const res = await fetch(`${API}/${open._id}/resolve`, { method: 'PATCH', headers, body: JSON.stringify({ status, note }) });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) throw new Error(j.message || 'Could not save.');
            flash(j.message); await openOne(open._id); load();
        } catch (e) { flash(e.message); } finally { setBusy(false); }
    };

    const exportCsv = () => {
        const rows = data?.data || [];
        if (!rows.length) return;
        const head = ['Date', 'Client', 'Mobile', 'Rating', 'What they chose', 'Comment', 'Stylist', 'Bill no.', 'Call requested', 'Call time', 'Went to Google', 'Resolved'];
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const csv = [head, ...rows.map((r) => [fmtDate(r.submittedAt), r.customerName, canSeePhone ? r.phone : maskPhone(r.phone), r.rating, (r.tags || []).join('; '), r.comment, (r.staff || []).join('; '), r.invoiceNo, r.wantsCallback ? 'Yes' : '', r.callbackTime, r.googleClicked ? 'Yes' : '', r.resolved ? 'Yes' : ''])].map((l) => l.map(esc).join(',')).join('\n');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
        a.download = `feedback-${days}-days.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const st = data?.stats;
    const maxDist = st ? Math.max(1, ...Object.values(st.dist)) : 1;

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <span className={styles.eyebrow}>Customer experience</span>
                    <h1>Feedback & reviews</h1>
                </div>
                <div className={styles.headerActions}>
                    <select className={styles.select} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Period">
                        {RANGES.map(([v, l]) => <option key={v} value={v}>Last {l}</option>)}
                    </select>
                    <button type="button" className={styles.ghostBtn} onClick={load} disabled={loading}><RefreshCw size={13} className={loading ? styles.spin : ''} /> Refresh</button>
                    <button type="button" className={styles.ghostBtn} onClick={exportCsv} disabled={!data?.data?.length}><Download size={13} /> Export</button>
                    <button type="button" className={styles.ghostBtn} onClick={() => setModal('settings')}><Settings2 size={13} /> Settings</button>
                    <button type="button" className={styles.primaryBtn} onClick={() => setModal('add')}><Plus size={14} /> Add feedback</button>
                </div>
            </header>

            {toast && <div className={styles.toast} role="status"><CheckCircle2 size={14} /> {toast}</div>}
            {error && <div className={`${styles.toast} ${styles.toastErr}`}><AlertCircle size={14} /> {error}</div>}

            <main className={styles.layout}>
                {/* ---------------- numbers ---------------- */}
                <aside className={styles.left}>
                    <div className={styles.metrics}>
                        <div className={styles.metric}><span className={styles.mIcon}><MessageSquareQuote size={14} /></span><h2>{st?.submitted ?? '–'}</h2><strong>Feedback received</strong><small>{st ? `${st.responseRate}% of ${st.sent} asked` : ''}</small></div>
                        <div className={styles.metric}><span className={styles.mIcon}><Star size={14} /></span><h2>{st?.avg || '–'}</h2><strong>Average rating</strong><small>out of 5</small></div>
                        <div className={styles.metric}><span className={`${styles.mIcon} ${styles.mGood}`}><Smile size={14} /></span><h2>{st ? `${st.happy}%` : '–'}</h2><strong>Happy clients</strong><small>{st ? `${st.googleClicks} went to Google` : ''}</small></div>
                        <div className={styles.metric}><span className={`${styles.mIcon} ${styles.mBad}`}><Clock size={14} /></span><h2>{st?.attention ?? '–'}</h2><strong>Needs attention</strong><small>{st ? `${st.callbacks} want a call` : ''}</small></div>
                    </div>

                    <div className={styles.box}>
                        <div className={styles.boxHead}><span className={styles.eyebrow}>Rating overview</span><h3>How clients rate you</h3></div>
                        <div className={styles.overview}>
                            <div className={styles.bigScore}>
                                <h1>{st?.avg || '0.0'}</h1>
                                <Stars value={st?.avg || 0} size={18} />
                                <small>{st?.submitted || 0} reviews</small>
                            </div>
                            <div className={styles.bars}>
                                {[5, 4, 3, 2, 1].map((n) => (
                                    <button type="button" key={n} className={`${styles.barRow} ${ratingFilter === n ? styles.barOn : ''}`} onClick={() => setRatingFilter(ratingFilter === n ? 0 : n)} title={`Show ${n}-star feedback`}>
                                        <span className={styles.barLabel}>{n} <Star size={10} className={styles.starOn} /></span>
                                        <span className={styles.barTrack}><span className={styles.barFill} style={{ width: `${((st?.dist?.[n] || 0) / maxDist) * 100}%` }} /></span>
                                        <span className={styles.barNum}>{st?.dist?.[n] || 0}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={styles.split}>
                        <div className={styles.box}>
                            <div className={styles.boxHead}><span className={styles.eyebrow}>Stylists</span><h3>Rating by stylist</h3></div>
                            <div className={styles.list}>
                                {(st?.staff || []).length === 0 && <p className={styles.empty}>No stylist ratings yet.</p>}
                                {(st?.staff || []).map((s) => (
                                    <button type="button" key={s.name} className={`${styles.staffRow} ${staffFilter === s.name ? styles.barOn : ''}`} onClick={() => setStaffFilter(staffFilter === s.name ? '' : s.name)}>
                                        <span className={styles.avatar}>{s.name.charAt(0)}</span>
                                        <span className={styles.staffName}>{s.name}<small>{s.count} review{s.count === 1 ? '' : 's'}</small></span>
                                        <span className={styles.staffAvg}>{s.avg} <Star size={11} className={styles.starOn} /></span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className={styles.box}>
                            <div className={styles.boxHead}><span className={styles.eyebrow}>What clients say</span><h3>Most chosen</h3></div>
                            <div className={styles.list}>
                                {(st?.topTags || []).length === 0 && <p className={styles.empty}>Nothing yet.</p>}
                                {(st?.topTags || []).map((t) => {
                                    const good = t.positive >= t.count / 2;
                                    return (
                                        <div key={t.tag} className={styles.tagRow}>
                                            {good ? <ThumbsUp size={13} className={styles.good} /> : <ThumbsDown size={13} className={styles.bad} />}
                                            <span>{t.tag}</span><b>{t.count}</b>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </aside>

                {/* ---------------- inbox ---------------- */}
                <section className={styles.inbox}>
                    <div className={styles.inboxHead}>
                        <div><span className={styles.eyebrow}>Feedback inbox</span><h3>Client feedback</h3></div>
                        <span className={styles.count}>{data?.total ?? 0} results</span>
                    </div>
                    <div className={styles.tabs} role="tablist">
                        {TABS.map(([id, label]) => (
                            <button type="button" role="tab" key={id} aria-selected={tab === id} className={`${styles.tab} ${tab === id ? styles.tabOn : ''}`} onClick={() => setTab(id)}>
                                {label}
                                {id === 'attention' && st?.attention > 0 && <span className={styles.badge}>{st.attention}</span>}
                                {id === 'callback' && st?.callbacks > 0 && <span className={styles.badge}>{st.callbacks}</span>}
                            </button>
                        ))}
                    </div>
                    <div className={styles.toolbar}>
                        <div className={styles.search}><Search size={14} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, comment, bill…" aria-label="Search feedback" /></div>
                        {(ratingFilter || staffFilter) && (
                            <button type="button" className={styles.filterChip} onClick={() => { setRatingFilter(0); setStaffFilter(''); }}>
                                {ratingFilter ? `${ratingFilter}★` : ''}{ratingFilter && staffFilter ? ' · ' : ''}{staffFilter} <X size={11} />
                            </button>
                        )}
                    </div>
                    <div className={styles.cards}>
                        {loading && !data && <div className={styles.empty}><Loader2 size={18} className={styles.spin} /></div>}
                        {data && data.data.length === 0 && <div className={styles.empty}><MessageSquareQuote size={26} /><p>No feedback here yet.</p></div>}
                        {(data?.data || []).map((f) => (
                            <button type="button" key={f.id} className={`${styles.card} ${f.rating <= 3 && !f.resolved ? styles.cardBad : ''}`} onClick={() => openOne(f.id)}>
                                <div className={styles.cardTop}>
                                    <span className={styles.avatar}>{String(f.customerName || '?').charAt(0)}</span>
                                    <span className={styles.who}><strong>{f.customerName || 'Client'}</strong><small>{canSeePhone ? f.phone : maskPhone(f.phone)}</small></span>
                                    <span className={styles.when}>{fmtDate(f.submittedAt)}</span>
                                </div>
                                <div className={styles.cardMid}>
                                    <Stars value={f.rating} />
                                    {f.comment ? <p>&ldquo;{f.comment}&rdquo;</p> : (f.tags || []).length ? null : <p className={styles.dim}>No comment</p>}
                                    {(f.tags || []).length > 0 && <div className={styles.tagLine}>{f.tags.slice(0, 3).map((t) => <span key={t} className={`${styles.tag} ${f.rating >= 4 ? styles.tagGood : styles.tagBad}`}>{t}</span>)}{f.tags.length > 3 && <span className={styles.tag}>+{f.tags.length - 3}</span>}</div>}
                                </div>
                                <div className={styles.cardBottom}>
                                    {(f.staff || []).length > 0 && <span className={styles.meta}><UserRound size={11} /> {f.staff.join(', ')}</span>}
                                    {f.wantsCallback && !f.resolved && <span className={`${styles.pill} ${styles.pillBad}`}><PhoneCall size={10} /> Call {f.callbackTime ? `(${f.callbackTime})` : ''}</span>}
                                    {f.googleClicked && <span className={`${styles.pill} ${styles.pillGood}`}>Google</span>}
                                    {f.rating <= 3 && <span className={`${styles.pill} ${f.resolved ? styles.pillGood : styles.pillWarn}`}>{f.resolved ? 'Resolved' : 'Open'}</span>}
                                    {f.source === 'MANUAL' && <span className={styles.pill}>By hand</span>}
                                </div>
                            </button>
                        ))}
                    </div>
                </section>
            </main>

            {/* ---------------- one feedback ---------------- */}
            {open && (
                <div className={styles.overlay} role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(null); }}>
                    <aside className={styles.drawer} role="dialog" aria-modal="true" aria-label="Feedback details">
                        <div className={styles.modalHead}><div><h3>Feedback</h3><p>Everything the client told you</p></div><button type="button" className={styles.iconBtn} onClick={() => setOpen(null)} aria-label="Close"><X size={16} /></button></div>
                        {open.loading ? <div className={styles.pad}><Loader2 size={18} className={styles.spin} /></div> : open.error ? <div className={styles.pad}><p className={styles.err}>{open.error}</p></div> : (
                            <div className={styles.drawerBody}>
                                <div className={styles.profile}>
                                    <span className={`${styles.avatar} ${styles.avatarBig}`}>{String(open.customerName || '?').charAt(0)}</span>
                                    <div><h2>{open.customerName || 'Client'}</h2><small>{canSeePhone ? open.phone : maskPhone(open.phone)}</small></div>
                                </div>
                                {open.phone && canSeePhone && (
                                    <div className={styles.row}>
                                        <a className={styles.ghostBtn} href={`tel:+91${open.phone}`}><PhoneCall size={13} /> Call</a>
                                        <a className={styles.ghostBtn} href={`https://wa.me/91${open.phone}`} target="_blank" rel="noreferrer"><MessageCircle size={13} /> WhatsApp</a>
                                    </div>
                                )}
                                <div className={styles.ratingBox}><span>Rating</span><Stars value={open.rating} size={18} /></div>
                                {(open.tags || []).length > 0 && (
                                    <div><span className={styles.blockTitle}>{open.rating >= 4 ? 'What they loved' : 'What went wrong'}</span>
                                        <div className={styles.tagLine}>{open.tags.map((t) => <span key={t} className={`${styles.tag} ${open.rating >= 4 ? styles.tagGood : styles.tagBad}`}>{t}</span>)}</div>
                                    </div>
                                )}
                                <div><span className={styles.blockTitle}>Comment</span><p className={styles.quote}>{open.comment ? `“${open.comment}”` : 'No comment.'}</p></div>
                                {open.wantsCallback && <div className={styles.callNote}><PhoneCall size={14} /> Wants a call back{open.callbackTime ? `: ${open.callbackTime}` : ''}</div>}
                                <div className={styles.facts}>
                                    {open.invoiceNo && <div><Receipt size={12} /> Bill {open.invoiceNo}{open.billTotal ? ` · ₹${Number(open.billTotal).toLocaleString('en-IN')}` : ''}</div>}
                                    {(open.services || []).length > 0 && <div><Star size={12} /> {open.services.join(', ')}</div>}
                                    {(open.staff || []).length > 0 && <div><UserRound size={12} /> {open.staff.map((p) => p.name).join(', ')}</div>}
                                    {open.googleShown && <div><ExternalLink size={12} /> {open.googleClickedAt ? 'Went to Google to review' : 'Was asked to review on Google'}</div>}
                                </div>
                                <div><span className={styles.blockTitle}>Timeline</span>
                                    <ol className={styles.timeline}>{(open.timeline || []).map((t, i) => <li key={i}><b>{fmtDate(t.at)} {fmtTime(t.at)}</b> {t.text}</li>)}</ol>
                                </div>
                                <div className={styles.resolveBox}>
                                    <span className={styles.blockTitle}>What was done</span>
                                    <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="E.g. Called the client, offered 20% off next visit" aria-label="Note" />
                                    {open.resolution?.status === 'RESOLVED' ? (
                                        <div className={styles.row}>
                                            <span className={`${styles.pill} ${styles.pillGood}`}><CheckCircle2 size={11} /> Resolved{open.resolution.byName ? ` by ${open.resolution.byName}` : ''}</span>
                                            <button type="button" className={styles.ghostBtn} onClick={() => resolve('OPEN')} disabled={busy}>Reopen</button>
                                            <button type="button" className={styles.primaryBtn} onClick={() => resolve('RESOLVED')} disabled={busy || !note.trim()}>Update note</button>
                                        </div>
                                    ) : (
                                        <button type="button" className={styles.primaryBtn} onClick={() => resolve('RESOLVED')} disabled={busy || !note.trim()} style={{ width: '100%', justifyContent: 'center' }}>
                                            {busy ? <Loader2 size={13} className={styles.spin} /> : <CheckCircle2 size={14} />} Mark as resolved
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </aside>
                </div>
            )}

            {modal === 'settings' && <SettingsModal headers={headers} onClose={() => setModal('')} onSaved={(m) => { setModal(''); flash(m); }} />}
            {modal === 'add' && <AddModal headers={headers} onClose={() => setModal('')} onSaved={(m) => { setModal(''); flash(m); load(); }} />}
        </div>
    );
};

export default FeedbackManagement;
